import { readLocal, writeLocal } from '@ndrc/app-sdk/client';
import { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import {
  NO_OFFSET,
  cascadeOffset,
  clampOffset,
  hasBox,
  sameOffset,
  sameViewport,
} from './placement';
import type { NaturalRect, Viewport, WindowOffset } from './placement';

/**
 * **Où se pose une fenêtre, et jusqu'où on peut la pousser.**
 *
 * Le décalage n'est qu'un `transform` sur la fenêtre : elle reste centrée par `.desk` (grille), ce
 * décalage s'ajoute par-dessus. Aucun `position:absolute` à gérer, aucun état de mise en page à
 * casser.
 *
 * La géométrie — les bornes, la cascade, l'invariant — vit dans `placement.ts`, pure et testée
 * sans navigateur. Ce hook n'apporte que ce qui a besoin du DOM : **mesurer** la fenêtre, et
 * rejouer la borne au bon moment.
 *
 * **Mesurer, et le mot compte.** La place naturelle est lue sur `offsetLeft`/`offsetTop`/
 * `offsetWidth`/`offsetHeight` : la boîte de MISE EN PAGE, que le `transform` n'affecte pas. La
 * version précédente reconstituait cette place en soustrayant le décalage d'un
 * `getBoundingClientRect()` — un calcul qui ne tient que si le décalage est réellement appliqué.
 * Il ne l'est pas en plein écran (`AppWindow` omet alors le `transform`), et il ne l'est pas
 * davantage pour une fenêtre réduite, dont le rectangle écran est entièrement nul. Dans ces deux
 * cas la borne se trompait de repère et **déplaçait** la fenêtre au lieu de la retenir : mesuré à
 * 1366 × 768 le 7 septembre 2026, une fenêtre réduite dérivait de 8px en x et 38px en y à chaque
 * `resize`, et revenait du dock à left = 1957, top = 8618 — introuvable. Voir `placement.ts`.
 *
 * **Trois moments, une seule borne.** La cascade initiale, le glisser, et « garder la fenêtre à
 * portée » (chaque rendu qui change son état d'affichage, et chaque `resize`) empruntent tous
 * `clampOffset`. Une borne recopiée trois fois aurait divergé au premier changement.
 */

export interface WindowPlacementInput {
  storageKey?: string;
  /** **Cette** fenêtre est le plein écran : elle est étalée par le CSS, le glisser n'a pas de sens
   * et le décalage n'est pas appliqué. */
  readonly fullscreen: boolean;
  /**
   * Le **bureau** est en plein écran — cette fenêtre-ci, ou une autre.
   *
   * Pendant ce temps `.desk` perd ses marges (`padding: 0`) : la place naturelle de TOUTES les
   * fenêtres, y compris celles du fond, se déplace pour la durée de la projection. Borner contre
   * cette géométrie provisoire reviendrait à mémoriser un décalage calculé pour un bureau qui
   * n'existe plus dès la sortie. On ne borne donc pas pendant le plein écran ; on le refait à la
   * sortie, quand le bureau a retrouvé ses marges.
   */
  readonly deskFullscreen: boolean;
  /** Réduite : plus aucune boîte à mesurer (`display: none`). On ne la borne pas — et on la borne
   * de nouveau au rendu qui la fait réapparaître, avant la peinture. */
  readonly minimized: boolean;
  /** La place de la fenêtre dans la cascade (`DeskWindow.cascade`). 0 = centrée, comme la fenêtre
   * unique de la référence. */
  readonly cascade: number;
}

interface DragState {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startOffset: WindowOffset;
  readonly natural: NaturalRect;
}

export interface WindowDragHandlers {
  offset: WindowOffset;
  move: (x: number, y: number) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

/**
 * La boîte de mise en page de la fenêtre : sa place et sa taille **sans** le `transform`.
 *
 * `offsetLeft`/`offsetTop` sont comptés depuis l'`offsetParent` — ici le `body`, qui n'a ni marge
 * ni bordure (`@ndrc/ui`, `theme.css`) et n'est jamais défilé (le bureau est en `overflow: hidden`)
 * : ces coordonnées SONT donc les coordonnées écran de la fenêtre au repos. Une fenêtre réduite
 * rend 0 partout ; `hasBox` le reconnaît et la borne s'abstient.
 */
function naturalRectOf(el: HTMLElement): NaturalRect {
  return {
    left: el.offsetLeft,
    top: el.offsetTop,
    width: el.offsetWidth,
    height: el.offsetHeight,
  };
}

function viewportOf(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight };
}

export function useWindowDrag(
  windowRef: RefObject<HTMLElement | null>,
  placement: WindowPlacementInput,
): WindowDragHandlers {
  const { fullscreen, deskFullscreen, minimized, cascade } = placement;
  const [offset, setOffset] = useState<WindowOffset>(() => {
    const saved = readLocal<WindowOffset | null>(placement.storageKey ?? '', null);
    return saved && Number.isFinite(saved.x) && Number.isFinite(saved.y) ? saved : NO_OFFSET;
  });
  useEffect(() => {
    if (placement.storageKey) writeLocal(placement.storageKey, offset);
  }, [offset, placement.storageKey]);
  const drag = useRef<DragState | null>(null);
  const placed = useRef(
    placement.storageKey !== undefined && readLocal(placement.storageKey, null) !== null,
  );

  // La cascade est posée UNE fois, au montage, et jamais rejouée : une fenêtre que l'élève a
  // déplacée doit rester là où il l'a mise. `useLayoutEffect` plutôt qu'un effet ordinaire — le
  // décalage est appliqué avant la peinture, la fenêtre n'apparaît donc jamais centrée pour bondir
  // ensuite.
  useLayoutEffect(() => {
    if (placed.current) return;
    placed.current = true;
    const el = windowRef.current;
    if (el === null) return;
    const desired = cascadeOffset(cascade);
    if (sameOffset(desired, NO_OFFSET)) return;
    setOffset(clampOffset(naturalRectOf(el), desired, viewportOf()));
  }, [cascade, windowRef]);

  /**
   * **Garder la fenêtre à portée de la souris**, quoi qu'il arrive.
   *
   * Une seule chose peut mettre une fenêtre hors de portée sans qu'on l'ait touchée : **l'écran a
   * rétréci**. C'est donc la seule chose qui déclenche une reprise — `sameViewport`. Deux
   * conséquences, et les deux sont voulues :
   *
   * - un aller-retour en plein écran ne reborne rien. Le bureau y anime son `padding` sur 340 ms
   *   (`desk.css`) ; mesurer pendant cette animation faisait remonter la fenêtre du fond de 20
   *   pixels et glisser celle de devant de 26 à chaque projection, alors que `desk.css` promet
   *   qu'« elles reviennent exactement où elles étaient, déplacement compris ». L'écran n'a pas
   *   changé de taille : il n'y a rien à reprendre.
   * - une fenêtre réduite pendant que l'écran rétrécit n'est pas mesurable (`hasBox`), donc rien
   *   n'est enregistré ; c'est le rendu qui la fait réapparaître qui la remet dans les clous, avant
   *   la peinture.
   *
   * Aucun bornage non plus pendant le plein écran lui-même : la fenêtre y est étalée par le CSS, et
   * la géométrie du bureau n'est pas celle dans laquelle elle reviendra.
   *
   * `clampOffset` est idempotent et rend un décalage absolu : reposer le même état est sans effet,
   * et `sameOffset` évite d'en créer un nouvel objet pour rien — sans quoi cet effet relancerait un
   * rendu, qui relancerait cet effet.
   */
  const clampedFor = useRef<Viewport | null>(null);
  useLayoutEffect(() => {
    if (deskFullscreen) return;

    function keepReachable(): void {
      const el = windowRef.current;
      if (el === null) return;
      const viewport = viewportOf();
      const previous = clampedFor.current;
      if (previous !== null && sameViewport(previous, viewport)) return;

      const natural = naturalRectOf(el);
      // Réduite : on ne mesure rien, et on n'enregistre RIEN — pour que le rendu qui la réaffiche
      // constate que l'écran a changé depuis son dernier bornage, et la reborne.
      if (!hasBox(natural)) return;

      clampedFor.current = viewport;
      setOffset((current) => {
        const next = clampOffset(natural, current, viewport);
        return sameOffset(next, current) ? current : next;
      });
    }

    keepReachable();
    window.addEventListener('resize', keepReachable);
    return () => {
      window.removeEventListener('resize', keepReachable);
    };
  }, [deskFullscreen, minimized, windowRef]);

  // Le redimensionnement natif écrit width/height dans le style de la fenêtre sans
  // changer le viewport. Reprendre uniquement ces changements explicites évite de
  // mémoriser une position calculée pendant l’animation du bureau en plein écran.
  useLayoutEffect(() => {
    const el = windowRef.current;
    if (!el || fullscreen || deskFullscreen || minimized) return;
    let width = el.style.width;
    let height = el.style.height;
    const observer = new MutationObserver(() => {
      if (width === el.style.width && height === el.style.height) return;
      width = el.style.width;
      height = el.style.height;
      const natural = naturalRectOf(el);
      if (!hasBox(natural)) return;
      setOffset((current) => {
        const next = clampOffset(natural, current, viewportOf());
        return sameOffset(next, current) ? current : next;
      });
    });
    observer.observe(el, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [fullscreen, deskFullscreen, minimized, windowRef]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Plein écran : le déplacement n'a pas de sens (ADR desktop-reference.md, § « Plein écran »).
      if (fullscreen) return;
      // Les trois pastilles gèrent leur propre clic ; elles ne doivent jamais lancer un glisser.
      if ((event.target as HTMLElement).closest('button') !== null) return;
      // Bouton secondaire ou molette d'une souris : seul le clic principal glisse la fenêtre.
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const el = windowRef.current;
      if (el === null) return;
      drag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startOffset: offset,
        natural: naturalRectOf(el),
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [fullscreen, offset, windowRef],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (state === null || state.pointerId !== event.pointerId) return;
    setOffset(
      clampOffset(
        state.natural,
        {
          x: state.startOffset.x + (event.clientX - state.startX),
          y: state.startOffset.y + (event.clientY - state.startY),
        },
        viewportOf(),
      ),
    );
  }, []);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (state === null || state.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const move = (x: number, y: number) => {
    const el = windowRef.current;
    if (el && !fullscreen)
      setOffset((current) =>
        clampOffset(naturalRectOf(el), { x: current.x + x, y: current.y + y }, viewportOf()),
      );
  };
  return { offset, move, onPointerDown, onPointerMove, onPointerUp };
}
